<template>
  <section
    ref="containerRef"
    :id="id"
    aria-roledescription="carousel"
    class="explore-more-carousel pb-100 mb-300"
    role="region"
  >
    <header>
      <h2 class="headline-h4-headline">
        {{ $t('highfrequencygame.hub.exploremore.headline') }}
      </h2>
    </header>

    <LcCarousel
      v-bind="{ disableSwipe, slides }"
      :id="`${id}-carousel`"
      class="carousel"
      :key-extractor="(_, index) => String(index)"
    >
      <template #slide="{ slide }">
        <ExploreMoreTeaser class="explore-more-carousel__teaser" v-bind="slide" />
      </template>
    </LcCarousel>
  </section>
</template>

<script lang="ts" setup>
import type {
  ExploreMoreTeaserProps,
  ExploreMoreTeaserVariant,
} from '~/components/ExploreMoreTeaser/ExploreMoreTeaser.vue'

type Props = {
  variants: ExploreMoreTeaserVariant[]
}

const SLIDE_WIDTH = 244
const SLIDE_MARGIN = 8
const SLIDE_WIDTH_CSS = `${SLIDE_WIDTH}px`
const SLIDE_MARGIN_CSS = `${SLIDE_MARGIN}px`

const FOR_YOU_SLIDE = {
  variant: 'for-you',
  to: '/',
} as ExploreMoreTeaserProps

const DRAW_SLIDE = {
  variant: 'draw',
  to: '/draw',
} as ExploreMoreTeaserProps

const SCRATCH_SLIDE = {
  variant: 'scratch',
  to: '/scratch',
} as ExploreMoreTeaserProps

const KENO_SLIDE = {
  variant: 'keno',
  to: '/keno',
} as ExploreMoreTeaserProps

const { variants = [] } = defineProps<Props>()

const containerRef = useTemplateRef('containerRef')
const containerWidth = ref(0)

const id = useId()
const isScratchcardsEnabled = useIsFeatureFlagEnabled('scratchcards')
const isKenoEnabled = useIsFeatureFlagEnabled('keno')
const localePath = useLocalePath()

const disableSwipe = computed(() => {
  const count = slides.value.length
  const totalWidth = count * (SLIDE_WIDTH + SLIDE_MARGIN)
  return totalWidth <= containerWidth.value
})

const slides = computed(() => {
  const _slides = []

  if (variants.includes('for-you')) {
    _slides.push(FOR_YOU_SLIDE)
  }

  if (variants.includes('draw')) {
    _slides.push(DRAW_SLIDE)
  }

  if (isScratchcardsEnabled && variants.includes('scratch')) {
    _slides.push(SCRATCH_SLIDE)
  }

  if (isKenoEnabled && variants.includes('keno')) {
    _slides.push(KENO_SLIDE)
  }

  return _slides.map((slide) => ({
    ...slide,
    to: localePath(slide.to),
  }))
})

useResizeObserver(containerRef, ([entry]) => {
  containerWidth.value = entry.contentRect.width
})
</script>

<style lang="scss" scoped>
.explore-more-carousel {
  overflow: hidden;
  margin: token('size/space/gap/150') -#{token('size/space/gap/100')} token('size/space/gap/300');
  padding-left: token('size/space/gap/100');

  header {
    min-height: 2rem;
    margin-bottom: token('size/space/gap/100');
  }

  .carousel {
    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-carousel__container) {
      overflow: visible;
    }
  }

  &__teaser {
    min-width: v-bind(SLIDE_WIDTH_CSS);
    margin-right: v-bind(SLIDE_MARGIN_CSS);
  }
}
</style>
